import {generatePath} from 'react-router-dom';
import {debounce} from 'lodash-es';
import {v1 as uuidv1} from 'uuid';

import sdk from '@/sdk';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import routers, {buildPath} from '@/config/routers';
import kuPiper from '@/middleware/kuPiper';
import {sourceType} from '@/constants/common';

import {
    reportEntityShow,
    reportEntityClick,
    reportStarLinkShowDuration,
    reportEntityCardClick,
    reportEntityLike,
    reportEntityKuLinkClick,
    reportFeedback,
    reportFeedbackClick,
    reportLogoClick,
    reportEntityBlock,
    reportEntityShowScreen,
    reportEntityShowScreen2,
    reportPolyExpandClick,
    reportPolyLinkClick,
} from '@/buried/starLink';
import {recordShowMentionDocObserver} from '@/buried/aigc';

export interface EntityEventInfo {
    entityId?: string;
    entityName?: string;
    likeNum?: number;
    reason?: string;
    inputReason?: string;
    type?: 'card' | 'search' | string;
    mention?: string;
    isPublish?: string;
    offset?: number;
    userGuid?: string;
}

const pvId = uuidv1();
function report(list: any[] = []) {
    if (list.length === 0) {
        return;
    }
    const user = sdk.user;
    reportEntityShowScreen({
        tag_name: list.map((item: any) => item.entityName).join(','),
    });
    reportEntityShowScreen2({
        user_id: user?.userGuid,
        email: user?.email,
        // 当前PV生成的唯一ID
        sessionId: pvId,
        entityList: list.map((item: any) => ({
            mention: item.entityMention,
            entityId: item.entityId,
            entityName: item.entityName,
        })),
        pageUrl: window.location.href,
    });
}
export const recordShowEntityObserver: {
    [key: string]: any;
    reportedMap: {
        [key: string]: boolean;
    };
    paddingList: Array<{
        entityId: string;
        entityName: string;
        entityMention: string;
    }>;
} = {
    reportedMap: {},
    paddingList: [],
    report: () => {
        report(recordShowEntityObserver.paddingList);
        recordShowEntityObserver.paddingList = [];
    },
    debounceReport: debounce(() => {
        recordShowEntityObserver.report();
    }, 300),
    observe() {
        this.report();
        this.reportedMap = {};
        this.disconnect();
        this.observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const entityId = (entry.target as HTMLElement).dataset.entityId || '';
                    const entityName = (entry.target as HTMLElement).dataset.entityName || '';
                    const entityMention = (entry.target as HTMLElement).dataset.entityMention || '';

                    if (this.reportedMap[entityId]) {
                        return;
                    }

                    if (entityId && entityName) {
                        this.paddingList.push({
                            entityId,
                            entityName,
                            entityMention,
                        });
                        this.debounceReport();

                        this.reportedMap[entityId] = true;
                    }
                }
            });
        }, {threshold: [1]});

        const elList = document.querySelectorAll('[data-entity-id][data-entity-name]');
        if (elList.length > 0) {
            elList.forEach(el => {
                this.observer.observe(el);
            });
        }
    },
    disconnect() {
        this.observer && this.observer.disconnect();
    },
};

// 生命周期埋点：展现、时长
const starLinkEffect = (par: EntityEventInfo[]) => {
    // 10.2展现埋点
    const user = sdk.user;
    // const entityName = params?.entityName;
    // const entityMention = entityEl?.dataset?.entityMention;
    const {entityId, entityName, mention} = par[0];
    const opt = {
        tag_name: entityName,
        entityId,
        entityName,
        mention: mention,
        user_id: user?.userGuid,
        email: user?.email,
        pageUrl: window.location.href,
        sessionId: pvId, // 当前PV生成的唯一ID
    };
    reportEntityClick(opt);
    const startTime = Date.now();
    return () => {
        // 10.9曝光时长埋点
        const opt = {
            entityId,
            entityName,
            duration: Date.now() - startTime,
        };
        reportStarLinkShowDuration(opt);
    };
};

// 埋点配置
export const getHighlightConfig = (enable: boolean) => {
    return {
        onDomReady: () => {
            // 记录Mention文档的展现次数
            recordShowMentionDocObserver.start();
        },
        enable,
        // 10.6、10.8屏幕中埋点
        // 星链渲染完成，高亮完成执行，组件卸载的时候销毁
        onHCUpdate: () => {
            setTimeout(() => {
                recordShowEntityObserver.observe();
            }, 100);
        },
        // 10.1文档星链list获取完成后埋点
        getEntityList: (data: {entityList: EntityEventInfo}) => {
            reportEntityShow(data.entityList, pvId);
        },
        // 浮层卡片的生命周期副作用和 React.useEffect 接口相同，为了方便业务方利用闭包统计查看时长
        usePopperEffect: starLinkEffect,
        // 点击反馈按钮
        onFeedbackClick: (par: EntityEventInfo[]) => {
            const data = Object.assign({}, ...par);
            reportFeedbackClick(data);
        },
        // 提交反馈信息
        onFeedbackSubmit: (par: EntityEventInfo[]) => {
            reportFeedback(Object.assign({}, ...par));
        },
        // 点赞。点击有用按钮
        onLikedChange: (par: EntityEventInfo[]) => {
            const data = Object.assign({}, ...par);
            reportEntityLike({tag_name: data.entityName, like_sum: data.likeNum});
        },
        // 屏蔽词条
        onBlockEntity: (par: EntityEventInfo[]) => {
            const data = Object.assign({}, ...par);
            reportEntityBlock({tag_name: data.entityName});
        },
        // 点击百科search、释义card、【已有】
        openPageOfBaike: (par: EntityEventInfo[]) => {
            const baikeUrl = generatePath(routers.baike.entity, {name: par[0].entityName});
            const jumpProxy = kuPiper.runProxy(kuPiper.proxyNameMap.entityLinkBaiKeClickJump, (baikeUrl: string) => {
                window.open(baikeUrl + `?source=${sourceType[104]}`);
            });
            jumpProxy(baikeUrl);
            reportEntityCardClick({tag_name: par[0].entityName, type: par[1]});
        },
        // 点头像【onRelatedPersonClick】
        onRelatedPersonClick: async (par: EntityEventInfo[]) => {
            const data = Object.assign({}, ...par);
            const {userGuid} = data;
            reportEntityCardClick({tag_name: data.entityName, type: 'ref'});
            const result = await sdk.as.getUserInfoList([userGuid]);
            location.href = `baidu://message/?id=${result[0]?.rlName || ''}`;
        },
        // 链接跳转【onLinkClick】
        // 跳转外域：reportEntityCardClick
        // 链接跳转本地：if (url.includes(location.host)) reportEntityKuLinkClick + reportEntityCardClick
        onLinkClick: (par: EntityEventInfo[], url: string) => {
            const data = Object.assign({}, ...par);
            reportEntityCardClick({tag_name: data.entityName, type: 'ref'});
            if (url?.includes(location.host)) {
                reportEntityKuLinkClick({tag_name: data.entityName, ref_page_id: url});
            }
        },
        // 智能知识助手【reportLogoClick】【onPoweredByClick】
        onPoweredByClick: (par: EntityEventInfo[]) => {
            const repo = sdk.as.userRepository;
            if (repo) {
                const squareUrl = buildPath(routers.Knowledge.squareDocRootPath, {
                    spaceGuid: repo.spaceGuid,
                    groupGuid: repo.groupGuid,
                    repositoryGuid: repo.repositoryGuid,
                });
                window.open(squareUrl);
            }
            const data = Object.assign({}, ...par);
            reportLogoClick(data);
        },
        // 点击展开或收起多义词列表【onExpandPolyseme】
        onExpandPolyseme: (par: EntityEventInfo[]) => {
            reportPolyExpandClick({
                user_id: sdk.user?.userGuid,
                tag_name: par[0].entityName,
                // type=open是展开，type=closed是收起
                type: par[1],
            });
        },
        // 点击打开多义词文字链【onLinkPolyseme】
        onLinkPolyseme: (par: EntityEventInfo[]) => {
            const data = Object.assign({}, ...par);
            const {baikeOriginalUrl, entityName, entityId} = data;

            window.open(baikeOriginalUrl);

            reportPolyLinkClick({
                user_id: sdk.user?.userGuid,
                // 文字链entityName
                tag_name: entityName,
                // 文字链id
                ref_page_id: entityId,
            });
        },
    };
};
